/* ─── Project pages: interaction ────────────────────────────────
 *
 * Drives the four behaviours the project page system needs:
 *   1. scroll reveal
 *   2. the ambient sound control
 *   3. species filtering
 *   4. species story expansion
 *
 * Every section that needs any of this loads the file, so guard
 * against running twice, and re-run on Shopify's theme-editor
 * section events so the customiser stays live.
 * ─────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  if (window.__projectImmersiveLoaded) return;
  window.__projectImmersiveLoaded = true;

  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.documentElement.classList.add('project-js');

  /* ─── 1. Scroll reveal ──────────────────────────────────────── */

  function initReveal(scope) {
    var targets = scope.querySelectorAll('.project-reveal:not([data-revealed])');
    if (!targets.length) return;

    if (REDUCED || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.setAttribute('data-revealed', '');
        el.classList.add('is-revealed');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    targets.forEach(function (el) {
      el.setAttribute('data-revealed', '');
      observer.observe(el);
    });
  }

  /* ─── 2. Ambient sound ──────────────────────────────────────────
   * Never autoplays. Browsers block it without a gesture, and a page
   * that starts making noise on its own is a page people close.
   * ─────────────────────────────────────────────────────────────── */

  function initAmbience(scope) {
    var roots = scope.querySelectorAll('[data-project-ambience]:not([data-ambience-ready])');

    roots.forEach(function (root) {
      root.setAttribute('data-ambience-ready', '');

      var audio = root.querySelector('[data-project-ambience-audio]');
      var toggle = root.querySelector('[data-project-ambience-toggle]');
      var label = root.querySelector('[data-project-ambience-label]');
      var info = root.querySelector('[data-project-ambience-info]');
      var panel = root.querySelector('[data-project-ambience-panel]');

      if (!audio || !toggle) return;

      var target = parseFloat(root.dataset.volume);
      if (isNaN(target)) target = 0.55;

      var fadeMs = parseInt(root.dataset.fadeMs, 10);
      if (isNaN(fadeMs)) fadeMs = 1500;

      var playLabel = root.dataset.playLabel || 'Listen';
      var pauseLabel = root.dataset.pauseLabel || 'Sound on';
      var storageKey = root.dataset.storageKey || 'sbs-ambience';

      var fadeTimer = null;
      var wanted = false;

      function setLabel(text) {
        if (label) label.textContent = text;
      }

      function fadeTo(to, done) {
        if (fadeTimer) clearInterval(fadeTimer);

        if (fadeMs <= 0) {
          audio.volume = to;
          if (done) done();
          return;
        }

        var from = audio.volume;
        var start = Date.now();

        fadeTimer = setInterval(function () {
          var progress = Math.min((Date.now() - start) / fadeMs, 1);
          var next = from + (to - from) * progress;
          audio.volume = Math.max(0, Math.min(1, next));

          if (progress >= 1) {
            clearInterval(fadeTimer);
            fadeTimer = null;
            if (done) done();
          }
        }, 40);
      }

      function remember(on) {
        try {
          window.localStorage.setItem(storageKey, on ? 'on' : 'off');
        } catch (e) {
          /* Private mode or storage disabled. The control still works. */
        }
      }

      function start() {
        wanted = true;
        root.classList.add('is-loading');
        audio.volume = 0;

        var attempt = audio.play();

        if (!attempt || !attempt.then) {
          root.classList.remove('is-loading');
          root.classList.add('is-playing');
          fadeTo(target);
          return;
        }

        attempt
          .then(function () {
            root.classList.remove('is-loading');
            if (!wanted) {
              audio.pause();
              return;
            }
            root.classList.add('is-playing');
            toggle.setAttribute('aria-pressed', 'true');
            setLabel(pauseLabel);
            fadeTo(target);
            remember(true);
          })
          .catch(function () {
            /* Blocked, offline, or the file is missing. Fall back to
               the resting state rather than lying with a playing icon. */
            wanted = false;
            root.classList.remove('is-loading', 'is-playing');
            toggle.setAttribute('aria-pressed', 'false');
            setLabel(playLabel);
          });
      }

      function stop(persist) {
        wanted = false;
        root.classList.remove('is-loading');
        toggle.setAttribute('aria-pressed', 'false');
        setLabel(playLabel);

        fadeTo(0, function () {
          audio.pause();
          root.classList.remove('is-playing');
        });

        if (persist !== false) remember(false);
      }

      function settle() {
        root.classList.remove('is-inviting');
      }

      toggle.addEventListener('click', function () {
        settle();
        if (root.classList.contains('is-playing') || root.classList.contains('is-loading')) {
          stop();
        } else {
          start();
        }
      });

      if (info && panel) {
        info.addEventListener('click', function () {
          settle();
          var open = info.getAttribute('aria-expanded') === 'true';
          info.setAttribute('aria-expanded', open ? 'false' : 'true');
          panel.hidden = open;
        });
      }

      /* Do not keep playing into a tab nobody is looking at. */
      document.addEventListener('visibilitychange', function () {
        if (!root.classList.contains('is-playing')) return;

        if (document.hidden) {
          fadeTo(0, function () {
            audio.pause();
          });
        } else if (wanted) {
          audio.play().then(function () {
            fadeTo(target);
          }).catch(function () {});
        }
      });

      /* Sound cannot start without a gesture, so the control asks for
         one. Anyone who has already turned it off is not asked again. */
      var previous = null;
      try {
        previous = window.localStorage.getItem(storageKey);
      } catch (e) {
        /* no-op */
      }

      if (previous !== 'off') {
        root.classList.add('is-inviting');
      }

      setLabel(playLabel);
    });
  }

  /* ─── 3. Species filtering and the row limit ────────────────────
   * The grid shows a set number of rows and folds the rest behind a
   * button. Rows are worked out from the cards' actual offsetTop
   * rather than from a card count, because the column count changes
   * with the viewport and the feature card spans two columns.
   * ─────────────────────────────────────────────────────────────── */

  function initLife(scope) {
    var sections = scope.querySelectorAll('[data-project-life]:not([data-life-ready])');

    sections.forEach(function (section) {
      section.setAttribute('data-life-ready', '');

      var buttons = section.querySelectorAll('[data-project-filter]');
      var cards = section.querySelectorAll('[data-project-species]');
      var more = section.querySelector('[data-project-life-more]');
      var fewer = section.querySelector('[data-project-life-fewer]');

      if (!cards.length) return;

      var initialRows = parseInt(section.dataset.initialRows, 10);
      if (isNaN(initialRows) || initialRows < 1) initialRows = 3;

      var rowsStep = parseInt(section.dataset.rowsStep, 10);
      if (isNaN(rowsStep) || rowsStep < 1) rowsStep = 2;

      /* How many rows are on show right now. Each click of "see more"
         adds a step rather than revealing the whole grid at once. */
      var visibleRows = initialRows;

      function applyRowLimit() {
        if (!more) return;

        cards.forEach(function (card) {
          card.classList.remove('is-beyond');
        });

        /* Measure every card before hiding any of them. Hiding a card
           reflows the grid, so reading offsetTop in the same pass moves
           the later cards up and lets them slip past the cutoff. */
        var measured = [];
        var tops = [];
        cards.forEach(function (card) {
          if (card.hidden) return;
          var top = Math.round(card.offsetTop);
          measured.push({ card: card, top: top });
          if (tops.indexOf(top) === -1) tops.push(top);
        });
        tops.sort(function (a, b) { return a - b; });

        var totalRows = tops.length;
        if (visibleRows > totalRows) visibleRows = totalRows;

        if (totalRows > visibleRows) {
          var cutoff = tops[visibleRows];
          measured.forEach(function (item) {
            if (item.top >= cutoff) item.card.classList.add('is-beyond');
          });
        }

        more.hidden = totalRows <= visibleRows;
        if (fewer) fewer.hidden = visibleRows <= initialRows;
      }

      function collapse() {
        visibleRows = initialRows;
        applyRowLimit();
        var top = section.getBoundingClientRect().top + window.pageYOffset - 80;
        window.scrollTo({ top: top, behavior: REDUCED ? 'auto' : 'smooth' });
      }

      if (more) {
        more.addEventListener('click', function () {
          visibleRows = visibleRows + rowsStep;
          applyRowLimit();
        });
      }

      if (fewer) {
        fewer.addEventListener('click', collapse);
      }

      buttons.forEach(function (button) {
        button.addEventListener('click', function () {
          var wanted = button.dataset.projectFilter;

          buttons.forEach(function (other) {
            var active = other === button;
            other.classList.toggle('is-active', active);
            other.setAttribute('aria-pressed', active ? 'true' : 'false');
          });

          cards.forEach(function (card) {
            var show = wanted === 'all' || card.dataset.group === wanted;
            card.hidden = !show;
          });

          /* A filter changes what lands on which row, so start again
             from the initial count rather than keeping a deep expansion. */
          visibleRows = initialRows;
          applyRowLimit();
        });
      });

      applyRowLimit();

      /* Images and web fonts both change row heights after first paint. */
      window.addEventListener('load', applyRowLimit);

      var resizeTimer = null;
      window.addEventListener('resize', function () {
        if (resizeTimer) clearTimeout(resizeTimer);
        resizeTimer = setTimeout(applyRowLimit, 150);
      });
    });
  }

  /* ─── 4. Species stories ────────────────────────────────────── */

  function initStories(scope) {
    var toggles = scope.querySelectorAll('[data-project-species-toggle]:not([data-story-ready])');

    toggles.forEach(function (toggle) {
      toggle.setAttribute('data-story-ready', '');

      var card = toggle.closest('[data-project-species]');
      if (!card) return;

      var story = card.querySelector('[data-project-species-story]');
      if (!story) return;

      toggle.addEventListener('click', function () {
        var open = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
        card.classList.toggle('is-open', !open);
        story.hidden = open;
      });
    });
  }

  /* ─── 5. Hero parallax ──────────────────────────────────────── */

  function initParallax(scope) {
    if (REDUCED) return;

    var layers = scope.querySelectorAll('[data-project-parallax]:not([data-parallax-ready])');
    if (!layers.length) return;

    layers.forEach(function (layer) {
      layer.setAttribute('data-parallax-ready', '');
    });

    if (window.__projectParallaxBound) return;
    window.__projectParallaxBound = true;

    var ticking = false;

    function update() {
      ticking = false;
      var offset = window.pageYOffset || document.documentElement.scrollTop;

      document.querySelectorAll('[data-parallax-ready]').forEach(function (layer) {
        var hero = layer.closest('.project-hero');
        if (!hero) return;

        var height = hero.offsetHeight;
        if (offset > height) return;

        layer.style.transform = 'translate3d(0, ' + offset * 0.18 + 'px, 0)';
      });
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  }

  /* ─── 6. Review expanders ───────────────────────────────────────
   * Review cards are clamped to a fixed number of lines so a row lines
   * up. The expander is only revealed where the text is actually being
   * cut off, so a two-line review does not get a pointless Read more.
   * ─────────────────────────────────────────────────────────────── */

  function initReviews(scope) {
    var cards = scope.querySelectorAll('[data-project-review]:not([data-review-ready])');
    if (!cards.length) return;

    cards.forEach(function (card) {
      card.setAttribute('data-review-ready', '');

      var quote = card.querySelector('[data-project-review-quote]');
      var toggle = card.querySelector('[data-project-review-toggle]');
      if (!quote || !toggle) return;

      function sync() {
        if (card.classList.contains('is-expanded')) return;
        /* A couple of pixels of slack: sub-pixel line heights make an
           unclipped element report a scrollHeight 1px over its client. */
        toggle.hidden = quote.scrollHeight - quote.clientHeight < 4;
      }

      toggle.addEventListener('click', function () {
        var open = card.classList.toggle('is-expanded');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        toggle.textContent = open
          ? toggle.dataset.less || 'Show less'
          : toggle.dataset.more || 'Read more';
        if (!open) sync();
      });

      sync();

      /* Web fonts land after first paint and change the line count. */
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(sync).catch(function () {});
      }
      window.addEventListener('load', sync);

      var resizeTimer = null;
      window.addEventListener('resize', function () {
        if (resizeTimer) clearTimeout(resizeTimer);
        resizeTimer = setTimeout(sync, 150);
      });
    });
  }

  /* ─── 7. Live figures ───────────────────────────────────────────
   * The map figures are rendered server-side from settings so they are
   * correct in the markup and need no JavaScript to appear. Where an
   * endpoint is configured they are then replaced with live values.
   *
   * The fetch is deferred until the block is near the viewport: the
   * stats payload is around 64KB gzipped because it carries every
   * protector record, and three numbers are not worth that on a page
   * nobody scrolls down. If anything fails the typed values stand.
   * ─────────────────────────────────────────────────────────────── */

  function initLiveFigures(scope) {
    var blocks = scope.querySelectorAll('[data-project-live-figures]:not([data-figures-ready])');

    blocks.forEach(function (block) {
      block.setAttribute('data-figures-ready', '');

      var endpoint = block.dataset.endpoint;
      var anonKey = block.dataset.anonKey;
      var cacheKey = block.dataset.cacheKey || 'community_page';
      if (!endpoint || !anonKey || !window.fetch) return;

      var targets = block.querySelectorAll('[data-figure]');
      if (!targets.length) return;

      function paint(globals) {
        targets.forEach(function (el) {
          var value;

          switch (el.dataset.figure) {
            case 'area_sqm':
              value = globals.total_area_sqm;
              break;
            case 'protectors':
              value = globals.total_protectors;
              break;
            case 'countries':
              /* The list mixes full names with two-letter codes for
                 countries it already lists by name, so the codes are
                 dropped rather than counted twice. */
              if (!Array.isArray(globals.countries)) return;
              value = globals.countries.filter(function (name) {
                return !(typeof name === 'string' && name.length === 2 && name === name.toUpperCase());
              }).length;
              break;
            default:
              return;
          }

          if (typeof value !== 'number' || !isFinite(value)) return;

          var text = value.toLocaleString('en-AU');
          if (el.dataset.suffix) text += ' ' + el.dataset.suffix;
          el.textContent = text;
        });
      }

      function load() {
        fetch(endpoint, {
          method: 'POST',
          headers: {
            apikey: anonKey,
            Authorization: 'Bearer ' + anonKey,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ cache_key: cacheKey })
        })
          .then(function (response) {
            if (!response.ok) throw new Error('stats ' + response.status);
            return response.json();
          })
          .then(function (data) {
            var globals = data && data.payload && data.payload.global;
            if (globals) paint(globals);
          })
          .catch(function () {
            /* Leave the server-rendered figures in place. */
          });
      }

      if (!('IntersectionObserver' in window)) {
        load();
        return;
      }

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            observer.disconnect();
            load();
          });
        },
        { rootMargin: '400px 0px' }
      );

      observer.observe(block);
    });
  }

  /* ─── Boot ──────────────────────────────────────────────────── */

  function init(scope) {
    var root = scope || document;
    initReveal(root);
    initAmbience(root);
    initLife(root);
    initStories(root);
    initParallax(root);
    initReviews(root);
    initLiveFigures(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
